import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";

import { AppBar, Box, Toolbar, IconButton, Badge, MenuItem, Menu, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonIcon from '@mui/icons-material/Person';

import * as sx from "./sx/ShopAppBarSx";

import { useAuth } from "../../../hooks/useAuth";
import { Storage } from '../../../utils/Storage';
import Search from './Search';
import LogoAsLink from './LogoAsLink';

import { selectShowFilterIcon } from '../../../store/layoutSlice';
import { selectItemsInCart } from '../../../store/cartSlice';
import { selectUserVname, selectUserName, selectUserEmail } from '../../../store/settingsSlice';
import { selectAvailableLanguages } from '../../../store/languageSlice';
import LanguageSwitcher from './LanguageSwitcher';

export default function ShopAppBar(props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { onLogout } = useAuth();

    const token = Storage.getAuthToken();
    const showFilterIcon = useSelector(selectShowFilterIcon);
    const itemsInCart = useSelector(selectItemsInCart);
    const userVname = useSelector(selectUserVname);
    const userName = useSelector(selectUserName);
    const userEmail = useSelector(selectUserEmail);
    const availableLanguages = useSelector(selectAvailableLanguages);
    const { handleDrawerToggle, setAppBarHeight } = props;
    const appBarRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);

    const isMenuOpen = Boolean(anchorEl);
    const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

    useEffect(() => {
        if (!appBarRef.current) {
            return;
        }

        const updateAppBarHeight = () => {
            if (appBarRef.current) {
                setAppBarHeight(appBarRef.current.getBoundingClientRect().height);
            }
        };

        updateAppBarHeight();
        const animationFrame = requestAnimationFrame(updateAppBarHeight);
        let resizeObserver;

        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(updateAppBarHeight);
            resizeObserver.observe(appBarRef.current);
        }

        window.addEventListener('resize', updateAppBarHeight);

        return () => {
            cancelAnimationFrame(animationFrame);
            window.removeEventListener('resize', updateAppBarHeight);
            resizeObserver && resizeObserver.disconnect();
        };
    }, [setAppBarHeight]);

    const handleProfileMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMobileMenuClose = () => {
        setMobileMoreAnchorEl(null);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        handleMobileMenuClose();
    };

    const handleLogout = () => {
        setAnchorEl(null);
        handleMobileMenuClose();

        onLogout();
    };

    const handleLogin = () => {
        setAnchorEl(null);
        handleMobileMenuClose();

        navigate("/login");
    };

    const menuId = 'primary-search-account-menu';
    const renderMenu = (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            id={menuId}
            keepMounted
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            open={isMenuOpen}
            onClose={handleMenuClose}
        >
            { /* Only login/logout for now */ false && <MenuItem onClick={handleMenuClose}>Profile</MenuItem> }
            { /* Only login/logout for now */ false && <MenuItem onClick={handleMenuClose}>My account</MenuItem> }
            { token ? <MenuItem onClick={handleLogout}>{t("AppBar.logout")}</MenuItem> : <MenuItem onClick={handleLogin}>{t("AppBar.login")}</MenuItem> }
        </Menu>
    );

    const mobileMenuId = 'primary-search-account-menu-mobile';
    const renderMobileMenu = (
        <Menu
            anchorEl={mobileMoreAnchorEl}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            id={mobileMenuId}
            keepMounted
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            open={isMobileMenuOpen}
            onClose={handleMobileMenuClose}
        >
            <MenuItem>
                <IconButton
                    size="large"
                    aria-label="cart"
                    color="primary"
                    component={RouterLink} to={"/cart"}
                >
                    <Badge badgeContent={2} color="error">
                        <ShoppingCartIcon />
                    </Badge>
                </IconButton>
                <p>Cart</p>
            </MenuItem>
            <MenuItem onClick={handleProfileMenuOpen}>
                <IconButton
                    size="large"
                    aria-label="account of current user"
                    aria-controls="primary-search-account-menu"
                    aria-haspopup="true"
                    color="inherit"
                >
                    <PersonIcon />
                </IconButton>
                <p>Profile</p>
            </MenuItem>
        </Menu>
    );

    return (
        <>
            <AppBar ref={appBarRef} elevation={0} position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }} color="appBarBGColor" >
                <Toolbar sx={sx.toolbar}>
                    <LogoAsLink to="/" sx={sx.logoLink} />
                    <Box sx={sx.searchContainer}>
                        {showFilterIcon && <IconButton
                            size="large"
                            color="primary"
                            aria-label={t("AppBar.openFilters")}
                            onClick={handleDrawerToggle}
                            sx={sx.mobileIconButton}
                        >
                            <TuneIcon />
                        </IconButton>}
                        <Search />
                    </Box>
                    <Box sx={sx.headerActions}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {Object.keys(availableLanguages).length > 1 && <Box sx={sx.languageSwitcher}><LanguageSwitcher /></Box>}
                            { itemsInCart > 0 && <IconButton
                                size="large"
                                edge="end"
                                aria-label="cart"
                                color="primary"
                                component={RouterLink} to={"/cart"}
                                sx={{ mr: 1 }}
                            >
                                <Badge badgeContent={itemsInCart} color="error">
                                    <ShoppingCartIcon />
                                </Badge>
                            </IconButton> }
                            {token && (userVname || userName || userEmail) && (
                                <Box sx={{ display: { xs: 'none', sm: 'block' }, mr: 2, textAlign: 'right' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 0.75 }}>
                                        {userVname} {userName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {userEmail}
                                    </Typography>
                                </Box>
                            )}
                            <IconButton
                                size="large"
                                edge="end"
                                aria-label="account of current user"
                                aria-controls={menuId}
                                aria-haspopup="true"
                                onClick={token ? handleProfileMenuOpen : handleLogin}
                                color={token ? "primary" : "default"}
                            >
                                <PersonIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </Toolbar>
            </AppBar>
            {renderMobileMenu}
            {renderMenu}
        </>
    );
}
